import React, { useEffect, useState } from "react";
import {
  FaVideo,
  FaExclamationCircle,
  FaInfoCircle,
  FaRobot,
  FaCog,
} from "react-icons/fa";
import "../index.css";
import { backendURL } from "../../../config/constants";
import AllowedDomains from "../AllowedDomains";
import SupportedLanguagesTable from "../SupportedLanguagesTable";

const AgentProcessVideo = () => {
  const [showResponse, setShowResponse] = useState(true);
  const [showError, setShowError] = useState(false);
  const [languages, setLanguages] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);

  useEffect(() => {
    const fetchLanguages = async () => {
      try {
        setLoading(true);
        const res = await fetch(`${backendURL}/agents/supported-languages/`);
        if (!res.ok) throw new Error(`HTTP Error: ${res.status}`);
        const data = await res.json();

        // Expected format: { details: [{ code: "eng", name: "English" }, ...] }
        if (Array.isArray(data.details)) {
          setLanguages(data.details);
        } else {
          setLanguages([]);
        }
      } catch (err) {
        console.error("Error fetching languages:", err);
        setError("Failed to load supported languages.");
      } finally {
        setLoading(false);
      }
    };

    fetchLanguages();
  }, []);

  const copyToClipboard = (text) => {
    navigator.clipboard.writeText(text);
    // In a real app, you would show a toast or notification here
  };

  return (
    <div className="docs-page">
      <div className="docs-page-header">
        <h1 className="docs-page-title">Process Video</h1>
        <p className="docs-page-description">
          Analyze video content for face detection and face analysis, and
          generate personalized recommendations.
        </p>
      </div>

      <div className="docs-section">
        <div className="docs-endpoint">
          <div className="docs-endpoint-header">
            <span className="docs-endpoint-method post">POST</span>
            <code className="docs-endpoint-url">
              /api/agents/process-video/
            </code>
          </div>

          <div className="docs-endpoint-meta">
            <span className="docs-endpoint-auth">
              <FaInfoCircle /> Authentication Required
            </span>
          </div>

          <h3 className="docs-subsection-title">Description</h3>
          <p className="docs-endpoint-description">
            This endpoint allows you to upload a video file for analysis. The
            API processes the video to extract the highest quality frame,
            analyzes it using an AI-based visual analysis engine, and returns
            an AI-powered analysis of its content.
          </p>

          <p className="supported-lang">
            <strong className="supported-color">Supported Languages:</strong>{" "}
            {loading ? (
              <span>Loading...</span>
            ) : error ? (
              <span className="error-text">{error}</span>
            ) : languages.length > 0 ? (
              languages.map((lang, index) => (
                <span key={lang.code}>
                  {lang.name}
                  {index < languages.length - 1 && ", "}
                </span>
              ))
            ) : (
              <span>
                English, Italian, French, Spanish, German, Portuguese, Arabic,
                Chinese (Simplified), Polish
              </span>
            )}
          </p>

          <AllowedDomains />

          <div className="docs-alert docs-alert-info">
            <FaInfoCircle className="docs-alert-icon" />
            <div className="docs-alert-content">
              <div className="docs-alert-title">
                AI-based Visual Analysis Engine
              </div>
              <p className="docs-alert-message">
                This endpoint leverages advanced AI models to analyze the
                extracted frame, providing insights that can be used for content
                categorization, object detection, and scene understanding.
              </p>
            </div>
          </div>

          <h3 className="docs-subsection-title">Request</h3>
          <p>
            This endpoint accepts <code>multipart/form-data</code> content type
            for file uploads.
          </p>

          <div className="docs-table">
            <h4 className="docs-response-title">Input Parameters</h4>
            <table>
              <thead>
                <tr>
                  <th>Field</th>
                  <th>Type</th>
                  <th>Required</th>
                  <th>Description</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>domain</td>
                  <td>
                    <code>string</code>
                  </td>
                  <td>
                    <span className="docs-badge docs-badge-required">
                      Required
                    </span>
                  </td>
                  <td>One of the domain values listed above</td>
                </tr>
                <tr>
                  <td>lang</td>
                  <td>
                    <code>string</code>
                  </td>
                  <td>
                    <span className="docs-badge">Not Required</span>
                  </td>
                  <td>
                    One of the nine codes in the table below (default{" "}
                    <code>eng</code>). English is <code>eng</code>;{" "}
                    <code>en</code> is not accepted here.
                  </td>
                </tr>
                <tr>
                  <td>video_file</td>
                  <td>
                    <code>file</code>
                  </td>
                  <td>
                    <span className="docs-badge docs-badge-required">
                      Required
                    </span>
                  </td>
                  <td>Video file for analysis (webm, mp4, etc.)</td>
                </tr>
              </tbody>
            </table>
          </div>

          <SupportedLanguagesTable />

          <h4 className="docs-response-title">Validation Rules</h4>
          <ul className="docs-list">
            <li>
              File must not exceed the configured maximum upload size of 5 MB
            </li>
            <li>
              File type must be one of the allowed video types: 'video/webm',
              'video/mp4', 'video/quicktime'
            </li>
          </ul>

          <div className="docs-code">
            <div className="docs-code-header">
              <span>Example Requests</span>
              <button
                className="docs-code-copy"
                onClick={() =>
                  copyToClipboard(`curl -X POST https://saas.todoai.ai/api/agents/process-video/ \\
  -H "X-API-Key: pk_YOUR_API_KEY" \\
  -H "X-Signature: $SIGNATURE" \\
  -F "domain=YOUR_DOMAIN" \\
  -F "lang=eng" \\
  -F "video_file=@/path/to/video.webm"`)
                }
              >
                Copy
              </button>
            </div>
            <pre>
              {`curl -X POST https://saas.todoai.ai/api/agents/process-video/ \\
  -H "X-API-Key: pk_YOUR_API_KEY" \\
  -H "X-Signature: $SIGNATURE" \\
  -F "domain=YOUR_DOMAIN" \\
  -F "lang=eng" \\
  -F "video_file=@/path/to/video.webm"`}
            </pre>
          </div>

          <h3 className="docs-subsection-title">Response</h3>

          <div className="docs-tabs">
            <div className="docs-tabs-header">
              <button
                className={`docs-tab-button ${showResponse ? "active" : ""}`}
                onClick={() => {
                  setShowResponse(true);
                  setShowError(false);
                }}
              >
                Success Response
              </button>
              <button
                className={`docs-tab-button ${showError ? "active" : ""}`}
                onClick={() => {
                  setShowResponse(false);
                  setShowError(true);
                }}
              >
                Error Response
              </button>
            </div>

            <div className="docs-tabs-content">
              {showResponse && (
                <div className="docs-tab-pane active">
                  <h4 className="docs-response-title">Response Fields</h4>
                  <div className="docs-table">
                    <table>
                      <thead>
                        <tr>
                          <th>Field</th>
                          <th>Type</th>
                          <th>Description</th>
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>analysis.status</td>
                          <td>
                            <code>string</code>
                          </td>
                          <td>Processing status (success/failure)</td>
                        </tr>
                        <tr>
                          <td>analysis.analysis</td>
                          <td>
                            <code>string</code>
                          </td>
                          <td>AI-generated face analysis</td>
                        </tr>
                        <tr>
                          <td>analysis.rag_query</td>
                          <td>
                            <code>string</code>
                          </td>
                          <td>Context from knowledge base</td>
                        </tr>
                        <tr>
                          <td>analysis.analysis_timestamp</td>
                          <td>
                            <code>string</code>
                          </td>
                          <td>ISO 8601 timestamp</td>
                        </tr>
                        <tr>
                          <td>video_info.fps</td>
                          <td>
                            <code>float</code>
                          </td>
                          <td>Frames per second</td>
                        </tr>
                        <tr>
                          <td>video_info.frame_count</td>
                          <td>
                            <code>integer</code>
                          </td>
                          <td>Total frames in video</td>
                        </tr>
                        <tr>
                          <td>video_info.width</td>
                          <td>
                            <code>integer</code>
                          </td>
                          <td>Video width in pixels</td>
                        </tr>
                        <tr>
                          <td>video_info.height</td>
                          <td>
                            <code>integer</code>
                          </td>
                          <td>Video height in pixels</td>
                        </tr>
                      </tbody>
                    </table>
                  </div>

                  <div className="docs-code">
                    <div className="docs-code-header">
                      <span>Example Response</span>
                      <button
                        className="docs-code-copy"
                        onClick={() =>
                          copyToClipboard(`{
  "analysis": {
    "analysis": "A person is facing the camera in good lighting. Visible features: short dark hair, glasses, neutral expression.",
    "rag_query": "Products and services relevant to the detected features",
    "domain": "YOUR_DOMAIN",
    "analysis_timestamp": "2024-10-24T10:35:00+00:00",
    "status": "success"
  },
  "video_info": {
    "fps": 30.0,
    "frame_count": 450,
    "width": 1280,
    "height": 720
  },
  "domain": "YOUR_DOMAIN",
  "user_id": 123
}`)
                        }
                      >
                        Copy
                      </button>
                    </div>
                    <pre>
                      {`{
  "analysis": {
    "analysis": "A person is facing the camera in good lighting. Visible features: short dark hair, glasses, neutral expression.",
    "rag_query": "Products and services relevant to the detected features",
    "domain": "YOUR_DOMAIN",
    "analysis_timestamp": "2024-10-24T10:35:00+00:00",
    "status": "success"
  },
  "video_info": {
    "fps": 30.0,
    "frame_count": 450,
    "width": 1280,
    "height": 720
  },
  "domain": "YOUR_DOMAIN",
  "user_id": 123
}`}
                    </pre>
                  </div>

                  <div className="docs-alert docs-alert-info">
                    <FaInfoCircle className="docs-alert-icon" />
                    <div className="docs-alert-content">
                      <div className="docs-alert-title">Analysis Content</div>
                      <p className="docs-alert-message">
                        The content of the <code>analysis</code> object will
                        vary depending on the specific AI-based visual analysis
                        engine used and the content of the video frame. It
                        typically includes descriptive text about what was
                        detected in the frame.
                      </p>
                    </div>
                  </div>
                </div>
              )}

              {showError && (
                <div className="docs-tab-pane active">
                  <h4 className="docs-response-title">Error Responses</h4>

                  <div className="docs-alert docs-alert-error">
                    <FaExclamationCircle className="docs-alert-icon" />
                    <div className="docs-alert-content">
                      <div className="docs-alert-title">
                        400: Domain is required
                      </div>
                      <p className="docs-alert-message">
                        Missing domain parameter
                      </p>
                    </div>
                  </div>

                  <div className="docs-alert docs-alert-error">
                    <FaExclamationCircle className="docs-alert-icon" />
                    <div className="docs-alert-content">
                      <div className="docs-alert-title">
                        400: Invalid Language Parameter
                      </div>
                      <p className="docs-alert-message">
                        lang must be one of: eng, it, fr, es, de, pt, ar, zh-CN, pl
                      </p>
                    </div>
                  </div>

                  <div className="docs-alert docs-alert-error">
                    <FaExclamationCircle className="docs-alert-icon" />
                    <div className="docs-alert-content">
                      <div className="docs-alert-title">
                        400: Invalid Video file
                      </div>
                      <p className="docs-alert-message">
                        Video format not supported or corrupted
                      </p>
                    </div>
                  </div>

                  <div className="docs-alert docs-alert-error">
                    <FaExclamationCircle className="docs-alert-icon" />
                    <div className="docs-alert-content">
                      <div className="docs-alert-title">
                        400: Couldnot open Video File
                      </div>
                      <p className="docs-alert-message">
                        Video cannot be processed
                      </p>
                    </div>
                  </div>
                  <div className="docs-alert docs-alert-error">
                    <FaExclamationCircle className="docs-alert-icon" />
                    <div className="docs-alert-content">
                      <div className="docs-alert-title">
                        401: Authentication failed
                      </div>
                      <p className="docs-alert-message">
                        Invalid API key or signature
                      </p>
                    </div>
                  </div>
                  <div className="docs-alert docs-alert-error">
                    <FaExclamationCircle className="docs-alert-icon" />
                    <div className="docs-alert-content">
                      <div className="docs-alert-title">
                        500: Internal server error
                      </div>
                      <p className="docs-alert-message">
                        Server-side processing error
                      </p>
                    </div>
                  </div>

                  <div className="docs-code">
                    <div className="docs-code-header">
                      <span>Error Response Example</span>
                      <button
                        className="docs-code-copy"
                        onClick={() =>
                          copyToClipboard(`{
  "error": "Unsupported file type. Allowed types are: video/webm, video/mp4, video/quicktime"
}`)
                        }
                      >
                        Copy
                      </button>
                    </div>
                    <pre>
                      {`{
  "error": "Unsupported file type. Allowed types are: video/webm, video/mp4, video/quicktime"
}`}
                    </pre>
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      </div>

      <div className="docs-section">
        <h2 className="docs-section-title">
          <FaVideo /> Implementation Examples
        </h2>

        <div className="docs-card">
          <div className="docs-card-title">
            <FaRobot /> JavaScript Example
          </div>
          <div className="docs-card-content">
            <p>
              Here's an example of how to upload and process a video using
              JavaScript:
            </p>

            <div className="docs-code">
              <div className="docs-code-header">
                <span>JavaScript Upload Example</span>
                <button
                  className="docs-code-copy"
                  onClick={() =>
                    copyToClipboard(`// Run on your server (Node.js 18+). Never expose your secret key in the browser.
const crypto = require('crypto');
const fs = require('fs');

const apiKey = 'pk_YOUR_API_KEY';
const secretKey = 'YOUR_SECRET_KEY';
const domain = 'YOUR_DOMAIN';
const signature = crypto.createHmac('sha256', secretKey).update(domain).digest('hex');

// Create a FormData object to hold the file data
const formData = new FormData();
formData.append('video_file', new Blob([fs.readFileSync('video.webm')], { type: 'video/webm' }), 'video.webm');
formData.append('domain', domain);
formData.append('lang', 'eng');

// Make the API request
fetch('https://saas.todoai.ai/api/agents/process-video/', {
  method: 'POST',
  headers: {
    'X-API-Key': apiKey,
    'X-Signature': signature
    // Note: Do not set Content-Type header for FormData
  },
  body: formData
})
.then(response => {
  if (!response.ok) {
    return response.json().then(err => {
      throw new Error(err.error || 'Failed to process video');
    });
  }
  return response.json();
})
.then(data => {
  console.log('Analysis:', data.analysis.analysis);
  console.log('Video:', \`\${data.video_info.width}x\${data.video_info.height}, \${data.video_info.fps} fps\`);
})
.catch(error => {
  console.error('Error:', error.message);
});`)
                  }
                >
                  Copy
                </button>
              </div>
              <pre>
                {`// Run on your server (Node.js 18+). Never expose your secret key in the browser.
const crypto = require('crypto');
const fs = require('fs');

const apiKey = 'pk_YOUR_API_KEY';
const secretKey = 'YOUR_SECRET_KEY';
const domain = 'YOUR_DOMAIN';
const signature = crypto.createHmac('sha256', secretKey).update(domain).digest('hex');

// Create a FormData object to hold the file data
const formData = new FormData();
formData.append('video_file', new Blob([fs.readFileSync('video.webm')], { type: 'video/webm' }), 'video.webm');
formData.append('domain', domain);
formData.append('lang', 'eng');

// Make the API request
fetch('https://saas.todoai.ai/api/agents/process-video/', {
  method: 'POST',
  headers: {
    'X-API-Key': apiKey,
    'X-Signature': signature
    // Note: Do not set Content-Type header for FormData
  },
  body: formData
})
.then(response => {
  if (!response.ok) {
    return response.json().then(err => {
      throw new Error(err.error || 'Failed to process video');
    });
  }
  return response.json();
})
.then(data => {
  console.log('Analysis:', data.analysis.analysis);
  console.log('Video:', \`\${data.video_info.width}x\${data.video_info.height}, \${data.video_info.fps} fps\`);
})
.catch(error => {
  console.error('Error:', error.message);
});`}
              </pre>
            </div>
          </div>
        </div>

        <div className="docs-card">
          <div className="docs-card-title">
            <FaCog /> Best Practices
          </div>
          <div className="docs-card-content">
            <ul className="docs-list">
              <li>
                <strong>File Size:</strong> While the API supports files up to 5
                MB, smaller files will upload and process faster
              </li>
              <li>
                <strong>Video Quality:</strong> Higher quality videos generally
                result in better frame extraction and analysis
              </li>
              <li>
                <strong>Error Handling:</strong> Implement robust error handling
                for both client-side validation and API response errors
              </li>
              <li>
                <strong>Content Types:</strong> The API works best with videos
                that have clear, well-lit frames and minimal motion blur
              </li>
              <li>
                <strong>Processing Time:</strong> Be aware that processing time
                increases with video file size and complexity
              </li>
            </ul>
          </div>
        </div>
      </div>

      <h3 className="docs-subsection-title">Signature Creation</h3>
      {/* <p>Calculate signature (empty payload)</p> */}

      <div className="docs-code">
        <div className="docs-code-header">
          <span>Example Request</span>
          <button className="docs-code-copy">Copy</button>
        </div>
        <pre>
          {`SECRET_KEY="YOUR_SECRET_KEY"
DOMAIN="YOUR_DOMAIN"
SIGNATURE=$(echo -n "$DOMAIN" | openssl dgst -sha256 -hmac "$SECRET_KEY" | awk '{print $2}')

# In Python
domain = "YOUR_DOMAIN"
signature = hmac.new(
    SECRET_KEY.encode('utf-8'),
    domain.encode('utf-8'),
    hashlib.sha256
).hexdigest()

# Headers
headers = {
    "X-API-Key": "pk_YOUR_API_KEY",
    "X-Signature": signature
}
`}
        </pre>
      </div>
    </div>
  );
};

export default AgentProcessVideo;
